import type { JSX } from 'react';
import { useEffect, useState } from "react";
import { handleCallback } from "@clearpath/auth-client";
import { vendorFailureCopy, VENDOR_RETRY_SENTENCE } from "../lib/vendor-failure-copy";

/** ENG-2952 — the OIDC code exchange can fail with anything from a network
 *  error to a Cedar/RBAC denial on the resulting session; this surface has
 *  no subject worth naming, so it carries VENDOR_RETRY_SENTENCE as the
 *  generic and falls back to it whenever `vendorFailureCopy` returns null. */
const SIGN_IN_FAILURE_SENTENCE = `We could not finish signing you in. ${VENDOR_RETRY_SENTENCE}`;

let callbackPromise: Promise<void> | null = null;
function runCallbackOnce(): Promise<void> {
  if (!callbackPromise) {
    callbackPromise = handleCallback().then(
      () => undefined,
      (err) => {
        callbackPromise = null;
        throw err;
      },
    );
  }
  return callbackPromise;
}

/** OIDC PKCE callback for vendor.molaris.ai. Exchanges the code in the URL
 *  for tokens, then bounces to "/" so the app shell renders normally. */
export function AuthCallback(): JSX.Element {
  const [status, setStatus] = useState<"exchanging" | "done" | "error">(
    "exchanging",
  );
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        await runCallbackOnce();
        if (cancelled) return;
        setStatus("done");
        window.location.replace("/");
      } catch (err) {
        if (cancelled) return;
        setError(vendorFailureCopy(err, { generic: SIGN_IN_FAILURE_SENTENCE }));
        setStatus("error");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="auth-callback-shell">
      {status === "exchanging" && <p>Finishing sign-in…</p>}
      {status === "error" && (
        <>
          <p>Sign-in failed: {error ?? SIGN_IN_FAILURE_SENTENCE}</p>
          <a href="/">Try again</a>
        </>
      )}
    </div>
  );
}
